import { useState } from 'react';
import { Link, type LinkProps } from '@tanstack/react-router';
import { trpc, type RouterOutputs } from '@org/clients/school';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Skeleton,
} from '@org/ui';
import { Bell, CheckCheck, MessageSquareText, ClipboardCheck, Megaphone, type LucideIcon } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { enUS, ptBR } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';

type StudentNotification = RouterOutputs['notifications']['list']['notifications'][number];

const ICONS: Partial<Record<StudentNotification['type'], LucideIcon>> = {
  feedback_published: ClipboardCheck,
  exercise_comment: MessageSquareText,
  admin_message: Megaphone,
};

/** Admin broadcasts carry a free-form link: a page in this app ("/conquistas") or a full https URL. */
function getAdminMessageLink(notification: StudentNotification): string | null {
  if (notification.type !== 'admin_message') return null;
  const link = (notification.payload as Record<string, unknown>).link;
  return typeof link === 'string' && link.length > 0 ? link : null;
}

const isExternalLink = (link: string) => link.startsWith('https://');

/**
 * Where a notification takes the student. Each type carries enough in its
 * payload to land on the exact source: the report itself, or the spread (and
 * exercise) a teacher commented on.
 */
function getNotificationTarget(notification: StudentNotification): Pick<LinkProps, 'to' | 'params' | 'search'> {
  const payload = notification.payload as Record<string, unknown>;
  switch (notification.type) {
    case 'feedback_published': {
      const feedbackId = typeof payload.feedbackId === 'string' ? payload.feedbackId : undefined;
      return { to: '/feedback', search: { feedbackId } };
    }
    case 'exercise_comment': {
      const classId = typeof payload.classId === 'string' ? payload.classId : undefined;
      if (!classId) {
        return { to: '/classes', search: { filter: 'past' } };
      }
      const blockIdx = typeof payload.blockIdx === 'number' ? payload.blockIdx : 0;
      const exerciseId = typeof payload.exerciseId === 'string' ? payload.exerciseId : undefined;
      return {
        to: '/classes/$classId/block/$blockIdx',
        params: { classId, blockIdx: String(blockIdx) },
        search: { exercise: exerciseId },
      };
    }
    case 'admin_message': {
      const link = getAdminMessageLink(notification);
      // External links are rendered as a plain anchor (see the list below);
      // here we only route in-app paths. The cast is what makes an arbitrary
      // admin-typed path acceptable to the typed router.
      if (link && !isExternalLink(link)) {
        return { to: link as LinkProps['to'] };
      }
      return { to: '/' };
    }
    default:
      return { to: '/' };
  }
}

function NotificationText({ notification }: { notification: StudentNotification }) {
  const { t } = useTranslation('p2e');
  const payload = notification.payload as Record<string, unknown>;

  if (notification.type === 'feedback_published') {
    const key = payload.feedbackType === 'final' ? 'notifications.feedbackFinal' : 'notifications.feedbackMidterm';
    return <p className="text-sm text-gray-900">{t(key)}</p>;
  }

  if (notification.type === 'exercise_comment') {
    const teacherName = typeof payload.teacherName === 'string' && payload.teacherName ? payload.teacherName : null;
    const className = typeof payload.className === 'string' && payload.className ? payload.className : null;
    const preview = typeof payload.commentPreview === 'string' ? payload.commentPreview : null;
    return (
      <>
        <p className="text-sm text-gray-900">
          {teacherName && <span className="font-semibold">{teacherName} </span>}
          {className
            ? t('notifications.exerciseCommentIn', { className })
            : t('notifications.exerciseComment')}
        </p>
        {preview && <p className="mt-0.5 line-clamp-2 text-xs text-gray-500">“{preview}”</p>}
      </>
    );
  }

  if (notification.type === 'admin_message') {
    const title = typeof payload.title === 'string' && payload.title ? payload.title : null;
    const body = typeof payload.body === 'string' && payload.body ? payload.body : null;
    return (
      <>
        <p className="text-sm font-semibold text-gray-900">{title ?? t('notifications.newNotification')}</p>
        {body && <p className="mt-0.5 line-clamp-3 text-xs text-gray-600">{body}</p>}
      </>
    );
  }

  return <p className="text-sm text-gray-900">{t('notifications.newNotification')}</p>;
}

export function NotificationBell() {
  const { t, i18n } = useTranslation('p2e');
  const [isOpen, setIsOpen] = useState(false);
  const utils = trpc.useUtils();
  const dateFnsLocale = i18n.language?.startsWith('pt') ? ptBR : enUS;

  const { data: unreadData } = trpc.notifications.getUnreadCount.useQuery(undefined, {
    refetchInterval: 30_000,
  });

  const { data, isLoading } = trpc.notifications.list.useQuery(
    { limit: 20 },
    { enabled: isOpen, refetchInterval: isOpen ? 30_000 : false },
  );

  const invalidate = () => {
    void utils.notifications.list.invalidate();
    void utils.notifications.getUnreadCount.invalidate();
  };

  const markAsRead = trpc.notifications.markAsRead.useMutation({ onSettled: invalidate });
  const markAllAsRead = trpc.notifications.markAllAsRead.useMutation({ onSettled: invalidate });

  const unreadCount = unreadData?.count ?? 0;
  const notifications = data?.notifications ?? [];

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={
            unreadCount > 0
              ? t('notifications.bellWithUnread', { count: unreadCount })
              : t('notifications.bell')
          }
          className="relative h-11 w-11 shrink-0 rounded-full border border-rose-100 bg-white/80 text-[#cf2e2e] shadow-sm hover:bg-white"
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span
              aria-hidden="true"
              className="absolute -right-1 -top-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#cf2e2e] px-1 text-[10px] font-semibold text-white"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[min(22rem,calc(100vw-2rem))] bg-white/95 p-0">
        <div className="flex items-center justify-between px-3 py-2">
          <DropdownMenuLabel className="px-0">{t('notifications.title')}</DropdownMenuLabel>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-auto px-2 py-1 text-xs text-[#cf2e2e]"
              onClick={() => markAllAsRead.mutate()}
              disabled={markAllAsRead.isPending}
            >
              <CheckCheck className="mr-1 h-3 w-3" />
              {t('notifications.markAllRead')}
            </Button>
          )}
        </div>
        <DropdownMenuSeparator className="my-0" />

        <div className="max-h-[400px] overflow-y-auto">
          {isLoading ? (
            <div className="space-y-2 p-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-14 w-full" />
              ))}
            </div>
          ) : notifications.length === 0 ? (
            <div className="py-8 text-center text-gray-500">
              <Bell className="mx-auto h-8 w-8 opacity-40" />
              <p className="mt-2 text-sm">{t('notifications.empty')}</p>
            </div>
          ) : (
            <ul className="py-1">
              {notifications.map((notification) => {
                const Icon = ICONS[notification.type] ?? Bell;
                const target = getNotificationTarget(notification);
                const adminLink = getAdminMessageLink(notification);
                const externalHref = adminLink && isExternalLink(adminLink) ? adminLink : null;
                const handleOpen = () => {
                  setIsOpen(false);
                  if (!notification.read) {
                    markAsRead.mutate({ notificationId: notification.notificationId });
                  }
                };
                const rowClassName = `flex items-start gap-3 px-3 py-2.5 transition-colors hover:bg-rose-50 ${
                  notification.read ? '' : 'bg-rose-50/60'
                }`;
                const rowContent = (
                  <>
                      <span className="mt-0.5 text-[#cf2e2e]">
                        <Icon className="h-5 w-5" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <NotificationText notification={notification} />
                        <span className="mt-1 block text-xs text-gray-500">
                          {formatDistanceToNow(new Date(notification.createdAt), {
                            addSuffix: true,
                            locale: dateFnsLocale,
                          })}
                        </span>
                      </span>
                      {!notification.read && (
                        <span aria-hidden="true" className="mt-2 h-2 w-2 shrink-0 rounded-full bg-[#cf2e2e]" />
                      )}
                  </>
                );
                return (
                  <li key={notification.notificationId}>
                    {externalHref ? (
                      <a
                        href={externalHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={handleOpen}
                        className={rowClassName}
                      >
                        {rowContent}
                      </a>
                    ) : (
                      <Link {...target} onClick={handleOpen} className={rowClassName}>
                        {rowContent}
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
